import { useState } from "react";
import { Bell, LockKeyhole, RefreshCw, ShieldCheck } from "lucide-react";
import { post, RequestError } from "../../api";
import { Field } from "../../components/common";
import { Brand } from "../../components/ui";
import loginLandscape from "../../assets/illustrations/login-nordic.svg";
import type { User } from "../../types";

export function Splash() {
  return <div className="center-screen"><Brand compact/><div className="loader"/><span>Preparando o salão…</span></div>;
}

type LoginResult =
  | { user: User; csrfToken: string; mfaRequired?: false }
  | { mfaRequired: true; challenge: string; expiresAt: number };

export function Auth({setup,agentReady,onAuth}:{setup:boolean;agentReady:boolean;onAuth:(u:User,c:string)=>void}) {
  const [values,setValues]=useState({token:"",username:"",password:""});
  const [challenge,setChallenge]=useState("");
  const [code,setCode]=useState("");
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState("");

  const submit=async(e:React.FormEvent)=>{
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if(challenge){
        const result=await post<{user:User;csrfToken:string}>("/auth/mfa",{challenge,code});
        onAuth(result.user,result.csrfToken);
        return;
      }
      const result=await post<LoginResult>(setup?"/auth/setup":"/auth/login",setup?values:{username:values.username,password:values.password});
      if("mfaRequired" in result&&result.mfaRequired){
        setChallenge(result.challenge);
        setCode("");
      }else if("user" in result){
        onAuth(result.user,result.csrfToken);
      }
    }catch(e){
      setError(e instanceof RequestError?e.message:"Não foi possível conectar ao painel.");
    }finally{
      setBusy(false);
    }
  };

  return <main className="auth-page">
    <section className="auth-form-wrap">
      <div className="auth-form-shell">
        <Brand />
        <form className="auth-card" onSubmit={submit}>
          <div className="auth-heading"><span className="auth-symbol" aria-hidden="true">ᛟ</span><div><span className="eyebrow">ACESSO SEGURO</span><h1>{challenge?"Confirme sua identidade":setup?"Funde seu salão":"Bem-vindo de volta"}</h1><p>{challenge?"Digite o código do autenticador ou um código de recuperação.":setup?"Crie a conta proprietária. O token de instalação funciona uma única vez.":"Entre para administrar seus servidores e mundos."}</p></div></div>
          <div className={`agent-status ${agentReady ? "online" : "offline"}`}><i/><span><b>{agentReady ? "Host operacional" : "Host indisponível"}</b><small>{agentReady ? "Agente local conectado e pronto" : "O login funciona, mas operações serão bloqueadas"}</small></span></div>
          {!agentReady&&<div className="notice warning"><Bell size={17}/>Verifique o serviço do agente no host.</div>}
          {challenge?<Field label="Código de autenticação ou recuperação" value={code} onChange={setCode} placeholder="000000" autoComplete="one-time-code"/>:<>{setup&&<Field label="Token de configuração" value={values.token} onChange={token=>setValues({...values,token})} placeholder="Token exibido ao instalar" autoComplete="one-time-code"/>}<Field label="Usuário" value={values.username} onChange={username=>setValues({...values,username})} placeholder="seu usuário" autoComplete="username"/><Field label="Senha" value={values.password} onChange={password=>setValues({...values,password})} type="password" placeholder={setup?"12+ caracteres e 3 classes":"••••••••••••"} autoComplete={setup?"new-password":"current-password"}/></>}
          {error&&<div className="form-error" role="alert">{error}</div>}
          <button className="primary wide" disabled={busy||Boolean(challenge&&!code)}>{busy?<><RefreshCw className="spin" size={17}/>Aguarde…</>:challenge?"Confirmar e entrar":setup?"Criar conta Owner":"Entrar no painel"}</button>
          {challenge&&<button type="button" className="text-button auth-back" onClick={()=>{setChallenge("");setCode("");setError("")}}>Usar outra conta</button>}
          <div className="secure-note"><LockKeyhole size={15}/><span>Sessão HttpOnly protegida por CSRF. Nenhuma credencial padrão.</span></div>
        </form>
        <p className="auth-legal">Ferramenta independente de gerenciamento de servidores Valheim.</p>
      </div>
    </section>
    <section className="auth-visual" aria-label="Paisagem nórdica abstrata">
      <img src={loginLandscape} alt="Montanhas, pinheiros e um portal em uma costa nórdica sob neblina"/>
      <div className="auth-visual-copy"><span className="eyebrow">NORDIC HOSTING INTERFACE</span><h2>Seus mundos.<br/><em>Um só salão.</em></h2><p>Operações seguras, backups antes de mudanças e controle claro do seu host.</p><div className="auth-foot"><ShieldCheck size={17}/> Painel sem privilégios · Agente local isolado</div></div>
    </section>
  </main>;
}
